import { createSharedAction, createSharedActionService, createSharedActionModel } from '../shared/Action.js';

// No-op base class for mixin pattern
class NoOpBase {
    constructor(parameters) {}
}

// Create ActionModel first
const SharedActionModel = createSharedActionModel(Object);

// Create ActionService that uses SharedActionModel by default
class SharedActionService extends createSharedActionService(Object) {
    constructor(parameters = {}) {
        // PASSIVE PATTERN: Only add ActionModel if no model provided
        if (parameters.model) {
            // Model already provided, don't add ActionModel class
            super(parameters);
        } else {
            // No model provided, inject SharedActionModel as default
            super({ ...parameters, ActionModel: parameters?.ActionModel || SharedActionModel });
        }
    }
}

// Create Action class that uses SharedActionService by default
class SharedAction extends createSharedAction(NoOpBase) {
    constructor(parameters = {}) {
        // PASSIVE PATTERN: Only add ActionService if no service provided
        if (parameters.service) {
            // Service already provided, don't add ActionService class
            super(parameters);
        } else {
            // No service provided, inject SharedActionService as default
            super({ ...parameters, ActionService: parameters?.ActionService || SharedActionService });
        }
    }
}

export class Action extends SharedAction {
    constructor(parameters = {}) {
        // Check if service is already provided (from subclass like CutAction)
        if (parameters.service) {
            // PASSIVE PATTERN: Service already created by subclass, just pass it along
            const { ActionService: _, ...paramsWithoutClass } = parameters;
            super({ ...paramsWithoutClass, service: parameters.service });
        } else {
            // ACTIVE PATTERN: No service provided, create one
            const service = new ActionService(parameters);
            
            // Rule 4: Remove ActionService class from parameters before calling super()
            // to prevent parent from creating a new service
            const { ActionService: _, ...paramsWithoutClass } = parameters;
            super({ ...paramsWithoutClass, service });
        }
    }
    // Note: Lifecycle methods (request, receive, update, complete) are inherited from SharedAction
    // and properly delegate to ActionService. Do not stub them here.
}

export class ActionService extends SharedActionService {
    constructor(parameters = {}) {
        // PASSIVE PATTERN: Check if model already provided (from subclass like BuildActionService)
        if (parameters.model) {
            // Model already created by subclass, just pass it along
            const { ActionModel: _, ...paramsWithoutClass } = parameters;
            super({ ...paramsWithoutClass, model: parameters.model });
            return;
        }
        
        // ACTIVE PATTERN: No model provided, create client model before calling super()
        const model = new ActionModel(parameters);
        
        // Rule 4: Remove ActionModel class from parameters before calling super()
        const { ActionModel: _, ...paramsWithoutClass } = parameters;
        super({ ...paramsWithoutClass, model });
    }
}

export class ActionModel extends SharedActionModel {
    constructor(parameters) {
        super(parameters);
    }
}

